import {useMutation} from '@tanstack/react-query';
import {AxiosError, AxiosProgressEvent} from 'axios';
import {APIResponseType, HttpResponse, APIResponse} from './models';
import {apiClient} from '../foundation/apiClient';

type Action = 'ADD' | 'REMOVE';

interface MutationParams {
    action: Action;
    cb?: VoidFunction;
}

interface AddNewCatalogItemMutationParams extends MutationParams {
    updateUploadProgress: (progress: number) => void;
    file: File;
    title: string;
    subtitle: string;
    price: string;
}

interface DeactivateCatalogItemMutationParams extends MutationParams {
    id: string;
}

const createNewCatalogItem = async (
    title: string,
    subtitle: string,
    price: string,
    file: File,
    updateUploadProgress?: (percentage: number) => void,
) => {
    const path = 'catalog/update';
    const formData = new FormData();
    formData.append('action', 'ADD');
    formData.append('title', title);
    formData.append('description', subtitle);
    formData.append('price', price);
    formData.append('file', file);
    const data = await apiClient.post(path, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },

        onUploadProgress(progressEvent: AxiosProgressEvent) {
            if (progressEvent.total) {
                const percentage = (progressEvent.loaded / progressEvent.total) * 100;
                updateUploadProgress?.(percentage);
                if (percentage >= 100) {
                    // throwSnackbarMessage();
                }
            }
        },
    });
    return data.data;
};

const deactivateCatalogItem = async (catalogItemId: string) => {
    const path = 'catalog/update';
    const formData = new FormData();
    formData.append('action', 'REMOVE');
    formData.append('id', catalogItemId);
    const data = await apiClient.post(path, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data.data;
};

export const useCatalogSectionUpdates = () => {
    const {mutate, ...rest} = useMutation({
        mutationFn: (data: MutationParams) => {
            if (data.action === 'ADD') {
                const params = data as AddNewCatalogItemMutationParams;
                return createNewCatalogItem(
                    params.title,
                    params.subtitle,
                    params.price,
                    params.file,
                    params.updateUploadProgress,
                );
            } else {
                const params = data as DeactivateCatalogItemMutationParams;
                return deactivateCatalogItem(params.id);
            }
        },
        retry: false,
    });

    const addCatalogItem = ({
        data,
        throwSnackbarMessage,
    }: {
        data: AddNewCatalogItemMutationParams;
        throwSnackbarMessage: (message: string) => void;
    }) => {
        mutate(data, {
            onSuccess: async (response) => {
                if (response.type === APIResponseType.SuccessfulString) {
                    throwSnackbarMessage((response.data as HttpResponse).message);
                    data.cb?.();
                } else if (response.type === APIResponseType.ErrorString) {
                    throwSnackbarMessage((response.data as HttpResponse).message);
                }
            },
            onError: (error) => {
                if (error.name === 'AxiosError') {
                    const err = error as AxiosError;
                    const message = err.response?.data as APIResponse;
                    if (message.type === APIResponseType.ErrorString) {
                        throwSnackbarMessage((message.data as HttpResponse).message);
                    }
                } else {
                    throwSnackbarMessage?.(error.message);
                }
            },
        });
    };

    return {addCatalogItem, ...rest};
};
